import React, { useState, useEffect, useMemo } from 'react';
import { Card, Button, Input, message, Modal, Select, Table, Tag, Statistic, Row, Col, Divider, Space, Tooltip, Typography, Drawer } from 'antd';
import { CopyOutlined, PlusOutlined, DollarOutlined, TransactionOutlined, GiftOutlined, UserOutlined, MenuOutlined } from '@ant-design/icons';
import { inviteAPI, userAPI } from '../services/api';
import { InviteItem, InviteCode, InviteCodeListState, UserConfig, User } from '../types';
import { showErrorToast, showToast } from '../utils/toast';
import { useAuth } from '../contexts/AuthContext';
import { useTelegram } from '../contexts/TelegramContext';

const { Text, Title } = Typography;
const { Option } = Select;

export const Invite: React.FC = () => {
    const { user } = useAuth();
    const [loading, setLoading] = useState(true);
    const [inviteDetails, setInviteDetails] = useState<InviteItem[]>([]);
    const [inviteCodeList, setInviteCodeList] = useState<InviteCodeListState>({ codes: [], stat: [] });
    const [userConfig, setUserConfig] = useState<UserConfig | null>(null);
    const [transferModalVisible, setTransferModalVisible] = useState(false);
    const [withdrawModalVisible, setWithdrawModalVisible] = useState(false);
    const [transferAmount, setTransferAmount] = useState('');
    const [withdrawAccount, setWithdrawAccount] = useState('');
    const [withdrawMethod, setWithdrawMethod] = useState('');
    const [transferLoading, setTransferLoading] = useState(false);
    const [withdrawLoading, setWithdrawLoading] = useState(false);
    const [createCodeLoading, setCreateCodeLoading] = useState(false);
    const [commissionLoading, setCommissionLoading] = useState(false);
    const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0
    });
    const { isInTelegram } = useTelegram();
    const [botInfo, setBotInfo] = useState<any>(null);

    // 响应式断点
    const breakpoints = useMemo(() => ({
        isMobile: windowWidth < 640,
        isTablet: windowWidth >= 640 && windowWidth < 1024,
        isDesktop: windowWidth >= 1024
    }), [windowWidth]);

    useEffect(() => {
        const handleResize = () => {
            setWindowWidth(window.innerWidth);
        };

        if (typeof window !== 'undefined') {
            window.addEventListener('resize', handleResize);
            return () => window.removeEventListener('resize', handleResize);
        }
    }, []);

    useEffect(() => {
        fetchBotInfo();
        fetchData();
        fetchCommissionData(pagination.current, pagination.pageSize);
    }, []);

    const fetchBotInfo = async () => {
        try {
            const response = await userAPI.getBotInfo();
            setBotInfo(response.data.data);
        } catch (error) {
            console.error('获取机器人信息失败', error);
        }
    };

    const fetchData = async () => {
        try {
            setLoading(true);
            // const [detailsRes, codesRes,configRes] = await Promise.all([
            //     inviteAPI.inviteDetails(pagination.current, pagination.pageSize),
            //     inviteAPI.getInvites(),
            //     userAPI.getConfig()
            // ]);
            const [codesRes, configRes] = await Promise.all([
                inviteAPI.getInvites(),
                userAPI.getConfig()
            ]);

            // const commissionData = detailsRes.data.data;
            // setInviteDetails(Array.isArray(commissionData) ? commissionData : []);
            // setPagination(prev => ({
            //     ...prev,
            //     total: commissionData?.total || 0
            // }));
            setInviteCodeList(codesRes.data.data || { codes: [], stat: [] });
            const config = configRes.data.data || null;
            setUserConfig(config);

            // 设置默认收款方式
            if (config?.withdraw_methods && config.withdraw_methods.length > 0 && !withdrawMethod) {
                setWithdrawMethod(config.withdraw_methods[0]);
            }
        } catch (error: any) {
            showErrorToast(error.response?.data?.message || '获取数据失败');
        } finally {
            setLoading(false);
        }
    };

    const fetchCommissionData = async (current: number, pageSize: number) => {
        try {
            setCommissionLoading(true);
            const detailsRes = await inviteAPI.inviteDetails(current, pageSize);
            const commissionData = detailsRes.data.data;
            setInviteDetails(Array.isArray(commissionData) ? commissionData : []);
            setPagination(prev => ({
                ...prev,
                current,
                pageSize,
                total: commissionData?.total || 0
            }));
        } catch (error: any) {
            showErrorToast(error.response?.data?.message || '获取佣金记录失败');
        } finally {
            setCommissionLoading(false);
        }
    };

    const handleTransfer = async () => {
        if (!transferAmount || parseFloat(transferAmount) <= 0) {
            showErrorToast('请输入有效的划转金额');
            return;
        }

        if (user && parseFloat(transferAmount) * 100 > user.commission_balance) {
            showErrorToast('划转金额不能超过佣金余额');
            return;
        }

        try {
            setTransferLoading(true);
            await inviteAPI.transferToBalance(String(parseFloat(transferAmount) * 100));
            showToast('划转成功');
            setTransferModalVisible(false);
            setTransferAmount('');
            // 刷新用户信息
            window.location.reload();
        } catch (error: any) {
            showErrorToast(error.response?.data?.message || '划转失败');
        } finally {
            setTransferLoading(false);
        }
    };

    const handleWithdraw = async () => {
        if (!withdrawAccount || !withdrawMethod) {
            showErrorToast('请填写完整的提现信息');
            return;
        }

        try {
            setWithdrawLoading(true);
            await inviteAPI.withdraw(withdrawAccount, withdrawMethod);
            showToast('提现申请已提交');
            setWithdrawModalVisible(false);
            setWithdrawAccount('');
            setWithdrawMethod('');
        } catch (error: any) {
            showErrorToast(error.response?.data?.message || '提现申请失败');
        } finally {
            setWithdrawLoading(false);
        }
    };

    const handleCreateInviteCode = async () => {
        try {
            setCreateCodeLoading(true);
            await inviteAPI.createInvite();
            showToast('邀请码生成成功');
            fetchData(); // 刷新数据
        } catch (error: any) {
            showErrorToast(error.response?.data?.message || '生成邀请码失败');
        } finally {
            setCreateCodeLoading(false);
        }
    };

    const copyToClipboard = (text: string) => {
        navigator.clipboard.writeText(text).then(() => {
            showToast('复制成功');
        }).catch(() => {
            showErrorToast('复制失败');
        });
    };

    const copyInviteLink = (code: string) => {
        if (isInTelegram && botInfo?.username) {
            const inviteLink = `https://t.me/${botInfo.username}?start=${code}`;
            navigator.clipboard.writeText(inviteLink).then(() => {
                showToast('邀请链接复制成功');
            }).catch(() => {
                showErrorToast('复制失败');
            });
        } else {
            const currentDomain = window.location.origin;
            const inviteLink = `${currentDomain}/register?code=${code}`;
            navigator.clipboard.writeText(inviteLink).then(() => {
                showToast('邀请链接复制成功');
            }).catch(() => {
                showErrorToast('复制失败');
            });
        }
    };

    const formatDate = (timestamp: number) => {
        return new Date(timestamp * 1000).toLocaleString('zh-CN');
    };

    const formatAmount = (amount: number) => {
        return (amount / 100).toFixed(2);
    };

    // 邀请码表格列定义
    const inviteCodeColumns = useMemo(() => {
        const baseColumns = [
            {
                title: <span style={{ fontSize: breakpoints.isMobile ? '12px' : '14px' }}>邀请码</span>,
                dataIndex: 'code',
                key: 'code',
                width: breakpoints.isMobile ? 100 : 150,
                render: (code: string) => (
                    <Space size="small">
                        <Text
                            code
                            style={{
                                fontSize: breakpoints.isMobile ? '11px' : '13px',
                                wordBreak: 'break-all'
                            }}
                        >
                            {breakpoints.isMobile ? code.substring(0, 8) + '...' : code}
                        </Text>
                        <Tooltip title="复制邀请链接">
                            <Button
                                type="text"
                                size="small"
                                icon={<CopyOutlined style={{ fontSize: '12px' }} />}
                                onClick={() => copyInviteLink(code)}
                            />
                        </Tooltip>
                    </Space>
                ),
            },
            {
                title: <span style={{ fontSize: breakpoints.isMobile ? '12px' : '14px' }}>状态</span>,
                dataIndex: 'status',
                key: 'status',
                width: breakpoints.isMobile ? 50 : 80,
                render: (status: boolean) => (
                    <Tag
                        color={status === true ? 'green' : 'default'}
                        style={{ fontSize: breakpoints.isMobile ? '11px' : '12px' }}
                    >
                        {status === true ? '已使用' : '可使用'}
                    </Tag>
                ),
            },
            {
                title: <span style={{ fontSize: breakpoints.isMobile ? '12px' : '14px' }}>创建时间</span>,
                dataIndex: 'created_at',
                key: 'created_at',
                width: breakpoints.isMobile ? 90 : 150,
                render: (timestamp: number) => (
                    <span style={{ fontSize: breakpoints.isMobile ? '11px' : '13px' }}>
                        {breakpoints.isMobile
                            ? new Date(timestamp * 1000).toLocaleDateString('zh-CN')
                            : formatDate(timestamp)
                        }
                    </span>
                ),
            },
        ];
        return baseColumns;
    }, [breakpoints]);

    // 佣金记录表格列定义
    const commissionColumns = useMemo(() => {
        const baseColumns = [
            {
                title: <span style={{ fontSize: breakpoints.isMobile ? '12px' : '14px' }}>订单号</span>,
                dataIndex: 'trade_no',
                key: 'trade_no',
                width: breakpoints.isMobile ? 100 : 180,
                render: (trade_no: string) => (
                    trade_no != null ? (
                        <Text
                            code
                            style={{
                                fontSize: breakpoints.isMobile ? '10px' : '12px',
                                wordBreak: 'break-all'
                            }}
                        >
                            {breakpoints.isMobile ? trade_no.substring(0, 12) + '...' : trade_no}
                        </Text>
                    ) : (
                        <Text
                            code
                            style={{
                                fontSize: breakpoints.isMobile ? '10px' : '12px',
                                wordBreak: 'break-all'
                            }}
                        >
                            邀请注册奖励
                        </Text>
                    )
                ),
            },
            {
                title: <span style={{ fontSize: breakpoints.isMobile ? '12px' : '14px' }}>订单金额</span>,
                dataIndex: 'order_amount',
                key: 'order_amount',
                width: breakpoints.isMobile ? 70 : 120,
                render: (amount: number) => (
                    <Text style={{ fontSize: breakpoints.isMobile ? '11px' : '13px' }}>
                        {userConfig?.currency_symbol}{formatAmount(amount)}
                    </Text>
                ),
            },
            {
                title: <span style={{ fontSize: breakpoints.isMobile ? '12px' : '14px' }}>获得佣金</span>,
                dataIndex: 'get_amount',
                key: 'get_amount',
                width: breakpoints.isMobile ? 70 : 120,
                render: (amount: number) => (
                    <Text
                        type="success"
                        style={{ fontSize: breakpoints.isMobile ? '11px' : '13px' }}
                    >
                        {userConfig?.currency_symbol}{formatAmount(amount)}
                    </Text>
                ),
            },
            {
                title: <span style={{ fontSize: breakpoints.isMobile ? '12px' : '14px' }}>时间</span>,
                dataIndex: 'created_at',
                key: 'created_at',
                width: breakpoints.isMobile ? 80 : 150,
                render: (timestamp: number) => (
                    <span style={{ fontSize: breakpoints.isMobile ? '11px' : '13px' }}>
                        {breakpoints.isMobile
                            ? new Date(timestamp * 1000).toLocaleDateString('zh-CN')
                            : formatDate(timestamp)
                        }
                    </span>
                ),
            },
        ];
        return baseColumns;
    }, [breakpoints, userConfig]);

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            </div>
        );
    }

    return (
        <div className="px-4 sm:px-6 lg:px-8">

            <div className="mb-6 sm:mb-8">
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white transition-colors">邀请管理</h1>
                <p className="mt-1 text-sm text-gray-600 dark:text-gray-400 transition-colors">
                    邀请好友注册，获得丰厚佣金奖励
                </p>
            </div>

            {/* 佣金余额和操作 */}
            <Row gutter={[6, 6]} className="mb-3 sm:mb-4 lg:mb-6">
                <Col xs={24} sm={12} lg={6}>
                    <Card
                        size={breakpoints.isMobile ? 'small' : 'default'}
                        className="h-full"
                        style={{
                            padding: breakpoints.isMobile ? '8px' : '16px',
                            borderRadius: breakpoints.isMobile ? '8px' : '12px'
                        }}
                    >
                        <Statistic
                            title={
                                <span style={{
                                    fontSize: breakpoints.isMobile ? '12px' : '14px',
                                    color: '#666'
                                }}>
                                    佣金余额
                                </span>
                            }
                            value={formatAmount(user?.commission_balance || 0)}
                            prefix={userConfig?.currency_symbol}
                            valueStyle={{
                                color: '#3f8600',
                                fontSize: breakpoints.isMobile ? '16px' : breakpoints.isTablet ? '20px' : '24px',
                                fontWeight: 'bold'
                            }}
                        />
                        <div className="mt-2 space-y-1.5">
                            <Button
                                type="primary"
                                block
                                size={breakpoints.isMobile ? 'small' : 'middle'}
                                icon={<TransactionOutlined style={{ fontSize: breakpoints.isMobile ? '12px' : '14px' }} />}
                                onClick={() => setTransferModalVisible(true)}
                                style={{
                                    fontSize: breakpoints.isMobile ? '12px' : '14px',
                                    height: breakpoints.isMobile ? '28px' : '32px',
                                    borderRadius: breakpoints.isMobile ? '6px' : '8px'
                                }}
                            >
                                {breakpoints.isMobile ? '划转' : '划转到余额'}
                            </Button>
                            {userConfig && !userConfig.withdraw_close && (
                                <Button
                                    block
                                    size={breakpoints.isMobile ? 'small' : 'middle'}
                                    icon={<DollarOutlined style={{ fontSize: breakpoints.isMobile ? '12px' : '14px' }} />}
                                    onClick={() => {
                                        // 打开模态框时设置默认收款方式
                                        if (userConfig?.withdraw_methods && userConfig.withdraw_methods.length > 0 && !withdrawMethod) {
                                            setWithdrawMethod(userConfig.withdraw_methods[0]);
                                        }
                                        setWithdrawModalVisible(true);
                                    }}
                                    style={{
                                        fontSize: breakpoints.isMobile ? '12px' : '14px',
                                        height: breakpoints.isMobile ? '28px' : '32px',
                                        borderRadius: breakpoints.isMobile ? '6px' : '8px'
                                    }}
                                >
                                    {breakpoints.isMobile ? '提现' : '申请提现'}
                                </Button>
                            )}
                        </div>
                    </Card>
                </Col>

                <Col xs={24} sm={12} lg={6}>
                    <Card
                        size={breakpoints.isMobile ? 'small' : 'default'}
                        className="h-full"
                        style={{
                            padding: breakpoints.isMobile ? '8px' : '16px',
                            borderRadius: breakpoints.isMobile ? '8px' : '12px'
                        }}
                    >
                        <Statistic
                            title={
                                <span style={{
                                    fontSize: breakpoints.isMobile ? '12px' : '14px',
                                    color: '#666'
                                }}>
                                    注册用户数
                                </span>
                            }
                            value={(Array.isArray(inviteCodeList.stat) && inviteCodeList.stat.length > 0 ? Number(inviteCodeList.stat[0]) : 0) as number}
                            prefix={<UserOutlined style={{ color: '#1890ff' }} />}
                            valueStyle={{
                                fontSize: breakpoints.isMobile ? '16px' : breakpoints.isTablet ? '20px' : '24px',
                                fontWeight: 'bold'
                            }}
                        />
                    </Card>
                </Col>

                <Col xs={12} sm={12} lg={6}>
                    <Card
                        size={breakpoints.isMobile ? 'small' : 'default'}
                        className="h-full"
                        style={{
                            padding: breakpoints.isMobile ? '8px' : '16px',
                            borderRadius: breakpoints.isMobile ? '8px' : '12px'
                        }}
                    >
                        <Statistic
                            title={
                                <span style={{
                                    fontSize: breakpoints.isMobile ? '12px' : '14px',
                                    color: '#666'
                                }}>
                                    佣金比例
                                </span>
                            }
                            value={Array.isArray(inviteCodeList.stat) && inviteCodeList.stat.length > 3 ? Number(inviteCodeList.stat[3]) : 0}
                            suffix="%"
                            prefix={<GiftOutlined style={{ color: '#52c41a' }} />}
                            valueStyle={{
                                fontSize: breakpoints.isMobile ? '16px' : breakpoints.isTablet ? '20px' : '24px',
                                fontWeight: 'bold'
                            }}
                        />
                    </Card>
                </Col>

                <Col xs={12} sm={12} lg={6}>
                    <Card
                        size={breakpoints.isMobile ? 'small' : 'default'}
                        className="h-full"
                        style={{
                            padding: breakpoints.isMobile ? '8px' : '16px',
                            borderRadius: breakpoints.isMobile ? '8px' : '12px'
                        }}
                    >
                        <Statistic
                            title={
                                <span style={{
                                    fontSize: breakpoints.isMobile ? '12px' : '14px',
                                    color: '#666'
                                }}>
                                    累计佣金
                                </span>
                            }
                            value={formatAmount(Array.isArray(inviteCodeList.stat) && inviteCodeList.stat.length > 1 ? Number(inviteCodeList.stat[1]) : 0)}
                            prefix={userConfig?.currency_symbol}
                            valueStyle={{
                                color: '#1890ff',
                                fontSize: breakpoints.isMobile ? '16px' : breakpoints.isTablet ? '20px' : '24px',
                                fontWeight: 'bold'
                            }}
                        />
                    </Card>
                </Col>
            </Row>

            {/* 邀请码管理 */}
            <Card
                title={
                    <span style={{ fontSize: breakpoints.isMobile ? '14px' : '16px' }}>
                        邀请码管理
                    </span>
                }
                size={breakpoints.isMobile ? 'small' : 'default'}
                className="mb-3 sm:mb-4 lg:mb-6"
                style={{ borderRadius: breakpoints.isMobile ? '8px' : '12px' }}
                extra={
                    <Button
                        type="primary"
                        icon={<PlusOutlined style={{ fontSize: breakpoints.isMobile ? '12px' : '14px' }} />}
                        loading={createCodeLoading}
                        onClick={handleCreateInviteCode}
                        size={breakpoints.isMobile ? 'small' : 'middle'}
                        style={{
                            fontSize: breakpoints.isMobile ? '12px' : '14px',
                            height: breakpoints.isMobile ? '28px' : '32px',
                            borderRadius: breakpoints.isMobile ? '6px' : '8px'
                        }}
                    >
                        {breakpoints.isMobile ? '生成' : '生成邀请码'}
                    </Button>
                }
            >
                <div className="w-full overflow-x-auto" style={{ maxWidth: '100vw' }}>
                    <Table
                        dataSource={inviteCodeList.codes}
                        columns={inviteCodeColumns}
                        rowKey="id"
                        pagination={false}
                        scroll={{
                            x: breakpoints.isMobile ? 240 : 'max-content',
                            scrollToFirstRowOnChange: true
                        }}
                        size={breakpoints.isMobile ? 'small' : 'middle'}
                        style={{
                            minWidth: breakpoints.isMobile ? '240px' : 'auto',
                            width: '100%'
                        }}
                    />
                </div>
            </Card>

            {/* 佣金记录 */}
            <Card
                title={
                    <span style={{ fontSize: breakpoints.isMobile ? '14px' : '16px' }}>
                        佣金记录
                    </span>
                }
                size={breakpoints.isMobile ? 'small' : 'default'}
                style={{ borderRadius: breakpoints.isMobile ? '8px' : '12px' }}
            >
                <div className="w-full overflow-x-auto" style={{ maxWidth: '100vw' }}>
                    <Table
                        dataSource={inviteDetails}
                        columns={commissionColumns}
                        rowKey="id"
                        loading={commissionLoading}
                        scroll={{
                            x: breakpoints.isMobile ? 320 : 'max-content',
                            scrollToFirstRowOnChange: true
                        }}
                        size={breakpoints.isMobile ? 'small' : 'middle'}
                        style={{
                            minWidth: breakpoints.isMobile ? '320px' : 'auto',
                            width: '100%'
                        }}
                        pagination={{
                            onChange: async (page, pageSize) => {
                                await fetchCommissionData(page, pageSize);
                            },
                            onShowSizeChange: async (current, size) => {
                                await fetchCommissionData(current, size);
                            },
                            current: pagination.current,
                            pageSize: breakpoints.isMobile ? 5 : pagination.pageSize,
                            total: pagination.total,
                            showSizeChanger: !breakpoints.isMobile,
                            showQuickJumper: !breakpoints.isMobile,
                            showTotal: !breakpoints.isMobile ? (total) => `共 ${total} 条记录` : undefined,
                            simple: breakpoints.isMobile,
                            size: breakpoints.isMobile ? 'small' : 'default',
                            locale: {
                                items_per_page: '条/页',
                            }
                        }}
                    />
                </div>
            </Card>

            {/* 划转模态框 */}
            <Modal
                title={
                    <span style={{ fontSize: breakpoints.isMobile ? '16px' : '18px' }}>
                        划转到余额
                    </span>
                }
                open={transferModalVisible}
                onCancel={() => setTransferModalVisible(false)}
                width={breakpoints.isMobile ? '95%' : breakpoints.isTablet ? '80%' : 520}
                centered
                style={{
                    padding: breakpoints.isMobile ? '12px' : '20px',
                    borderRadius: breakpoints.isMobile ? '12px' : '16px'
                }}
                footer={
                    breakpoints.isMobile ? (
                        <div style={{ display: 'flex', gap: '8px', width: '100%' }}>
                            <Button
                                key="cancel"
                                onClick={() => setTransferModalVisible(false)}
                                size="large"
                                style={{
                                    fontSize: '14px',
                                    height: '40px',
                                    borderRadius: '8px',
                                    flex: 1
                                }}
                            >
                                取消
                            </Button>
                            <Button
                                key="submit"
                                type="primary"
                                loading={transferLoading}
                                onClick={handleTransfer}
                                size="large"
                                style={{
                                    fontSize: '14px',
                                    height: '40px',
                                    borderRadius: '8px',
                                    flex: 1
                                }}
                            >
                                确认划转
                            </Button>
                        </div>
                    ) : [
                        <Button
                            key="cancel"
                            onClick={() => setTransferModalVisible(false)}
                            size="middle"
                            style={{
                                fontSize: '16px',
                                height: '36px',
                                borderRadius: '6px'
                            }}
                        >
                            取消
                        </Button>,
                        <Button
                            key="submit"
                            type="primary"
                            loading={transferLoading}
                            onClick={handleTransfer}
                            size="middle"
                            style={{
                                fontSize: '16px',
                                height: '36px',
                                borderRadius: '6px'
                            }}
                        >
                            确认划转
                        </Button>,
                    ]
                }
            >
                <div style={{ padding: breakpoints.isMobile ? '8px 0' : '16px 0' }}>
                    <div style={{ marginBottom: '16px' }}>
                        <Text style={{ fontSize: breakpoints.isMobile ? '13px' : '15px' }}>
                            当前佣金余额: {userConfig?.currency_symbol}{formatAmount(user?.commission_balance || 0)}
                        </Text>
                    </div>
                    <div>
                        <Text style={{ fontSize: breakpoints.isMobile ? '13px' : '15px' }}>划转金额:</Text>
                        <Input
                            value={transferAmount}
                            onChange={(e) => setTransferAmount(e.target.value)}
                            placeholder="请输入划转金额"
                            prefix={userConfig?.currency_symbol}
                            type="number"
                            min="0"
                            step="0.01"
                            size={breakpoints.isMobile ? 'large' : 'middle'}
                            style={{
                                marginTop: '8px',
                                fontSize: breakpoints.isMobile ? '14px' : '16px',
                                borderRadius: breakpoints.isMobile ? '8px' : '6px'
                            }}
                        />
                    </div>
                </div>
            </Modal>

            {/* 提现模态框 */}
            <Modal
                title={
                    <span style={{ fontSize: breakpoints.isMobile ? '16px' : '18px' }}>
                        申请提现
                    </span>
                }
                open={withdrawModalVisible}
                onCancel={() => setWithdrawModalVisible(false)}
                width={breakpoints.isMobile ? '95%' : breakpoints.isTablet ? '80%' : 520}
                centered
                style={{
                    padding: breakpoints.isMobile ? '12px' : '20px',
                    borderRadius: breakpoints.isMobile ? '12px' : '16px'
                }}
                footer={
                    breakpoints.isMobile ? (
                        <div style={{ display: 'flex', gap: '8px', width: '100%' }}>
                            <Button
                                key="cancel"
                                onClick={() => setWithdrawModalVisible(false)}
                                size="large"
                                style={{
                                    fontSize: '14px',
                                    height: '40px',
                                    borderRadius: '8px',
                                    flex: 1
                                }}
                            >
                                取消
                            </Button>
                            <Button
                                key="submit"
                                type="primary"
                                loading={withdrawLoading}
                                onClick={handleWithdraw}
                                size="large"
                                style={{
                                    fontSize: '14px',
                                    height: '40px',
                                    borderRadius: '8px',
                                    flex: 1
                                }}
                            >
                                提交申请
                            </Button>
                        </div>
                    ) : [
                        <Button
                            key="cancel"
                            onClick={() => setWithdrawModalVisible(false)}
                            size="middle"
                            style={{
                                fontSize: '16px',
                                height: '36px',
                                borderRadius: '6px'
                            }}
                        >
                            取消
                        </Button>,
                        <Button
                            key="submit"
                            type="primary"
                            loading={withdrawLoading}
                            onClick={handleWithdraw}
                            size="middle"
                            style={{
                                fontSize: '16px',
                                height: '36px',
                                borderRadius: '6px'
                            }}
                        >
                            提交申请
                        </Button>,
                    ]
                }
            >
                <div style={{ padding: breakpoints.isMobile ? '8px 0' : '16px 0' }}>
                    <div style={{ marginBottom: '16px' }}>
                        <Text style={{ fontSize: breakpoints.isMobile ? '13px' : '15px' }}>收款方式:</Text>
                        <Select
                            value={withdrawMethod}
                            onChange={setWithdrawMethod}
                            placeholder="请选择收款方式"
                            style={{
                                width: '100%',
                                marginTop: '8px'
                            }}
                            size={breakpoints.isMobile ? 'large' : 'middle'}
                        >
                            {userConfig?.withdraw_methods?.map((method) => (
                                <Option key={method} value={method}>
                                    {method}
                                </Option>
                            ))}
                        </Select>
                    </div>
                    <div>
                        <Text style={{ fontSize: breakpoints.isMobile ? '13px' : '15px' }}>收款账号:</Text>
                        <Input
                            value={withdrawAccount}
                            onChange={(e) => setWithdrawAccount(e.target.value)}
                            placeholder="请输入收款账号"
                            size={breakpoints.isMobile ? 'large' : 'middle'}
                            style={{
                                marginTop: '8px',
                                fontSize: breakpoints.isMobile ? '14px' : '16px',
                                borderRadius: breakpoints.isMobile ? '8px' : '6px'
                            }}
                        />
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default Invite;